import React, { useCallback } from 'react';

import classnames from 'classnames';

import { PrimaryButton, TextButton } from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import { COMPONENTS, SECTIONS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import StickyContentMarker from 'components/Globals/StickyHeaders';

import { TP } from 'constants/index';
import { SUBSCRIPTION_GROUP_DETAILS, SUBSCRIPTION_GROUP_IDENTIFIERS } from 'constants/subscriptions';

import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';

import { useTranslation } from 'src/i18n';

import PriceRange from '../PriceRange';

import PlanGroupDetails from './PlanGroupDetails';
import classes from './PlanGroupListing.module.scss';

const PlanGroupActionButton = ({ identifier, onChooseHandler, isCompressed = false, trackingData = {} }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { isDesktop } = useDeviceTypeLayouts();

  if (isCompressed) {
    return (
      <TextButton
        onClick={() => onChooseHandler(identifier)}
        rightIcon={<SpriteIcon icon="chevron_right" />}
        shape="rectangle"
        variant="text"
        trackingData={{
          ...trackingData,
          subComponent: SUB_COMPONENTS.VIEW_MORE_CTA,
        }}
      >
        <Typography size={10} color="inherit" weight="bold">
          {t(`${TP}.MORE_INFO`)}
        </Typography>
      </TextButton>
    );
  }

  if (isDesktop) {
    return (
      <PrimaryButton
        onClick={() => onChooseHandler(identifier)}
        rightIcon={<SpriteIcon icon="chevron_right" />}
        shape="rectangle"
        stretch
      >
        {t(`${TP}.MORE_INFO`)}
      </PrimaryButton>
    );
  }

  return (
    <>
      <PriceRange price={SUBSCRIPTION_GROUP_DETAILS[identifier].price} priceSize={16} />
      <TextButton
        onClick={() => onChooseHandler(identifier)}
        rightIcon={<SpriteIcon icon="chevron_right" />}
        shape="rectangle"
        stopPropagation
      >
        <Typography size={10} color="inherit" weight="bold">
          {t(`${TP}.MORE_INFO`)}
        </Typography>
      </TextButton>
    </>
  );
};

const PlanGroupStickyPlanHeader = () => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { isDesktop } = useDeviceTypeLayouts();

  return (
    <div className={classes.root}>
      {Object.values(SUBSCRIPTION_GROUP_IDENTIFIERS).map(identifier => (
        <div key={identifier} className={classnames(classes.group, classes.stickyHeader)}>
          <Typography variant="p" weight={isDesktop ? 'bold' : 'medium'} size={isDesktop ? 16 : 13}>
            {t(SUBSCRIPTION_GROUP_DETAILS[identifier]?.title)}
          </Typography>
        </div>
      ))}
    </div>
  );
};

const PlanGroupStickyPlanFooter = ({ onChooseHandler }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');

  return (
    <div className={classes.root}>
      {Object.values(SUBSCRIPTION_GROUP_IDENTIFIERS).map(identifier => (
        <div key={identifier} className={classnames(classes.group, classes.stickyFooter)}>
          <PrimaryButton
            onClick={() => onChooseHandler(identifier)}
            rightIcon={<SpriteIcon icon="chevron_right" />}
            shape="rectangle"
            stretch
          >
            {t(`${TP}.MORE_INFO`)}
          </PrimaryButton>
        </div>
      ))}
    </div>
  );
};

const PlanGroupListing = ({ onChoose, isCompressed = false, trackingData = {} }) => {
  const { isDesktop } = useDeviceTypeLayouts();
  const track = useTracking();
  const { paywall } = useAppContext();

  const onChooseHandler = useCallback(
    identifier => {
      onChoose(identifier);

      track.click({
        section: SECTIONS.PAYWALL,
        component: COMPONENTS.PAYWALL_PLAN_GROUP_CARD,
        meta: {
          id: identifier,
          actionType: paywall?.paywallType,
        },
      });
    },
    [onChoose, track, paywall],
  );

  return (
    <div className={classnames(classes.root, { [classes.root__compressed]: isCompressed })}>
      {Object.values(SUBSCRIPTION_GROUP_IDENTIFIERS).map((identifier, index) => (
        <div
          key={identifier}
          className={classnames(classes.group, { [classes.group__compressed]: isCompressed })}
          onClick={isDesktop ? undefined : () => onChooseHandler(identifier)}
        >
          {isDesktop && index === 0 && (
            <StickyContentMarker
              stackIdentifier="primary"
              topPositionPx={75}
              name="plan-group-header"
              renderHeader={() => <PlanGroupStickyPlanHeader />}
            />
          )}
          <PlanGroupDetails
            identifier={identifier}
            showBasicInfo={!isDesktop || isCompressed}
            isGroupListingView
            trackingData={trackingData}
            {...(isCompressed ? { styles: { root: classes.detailsRoot, description: classes.description } } : {})}
          />
          <div className={classnames(classes.group__footer, { [classes.group__footer__compressed]: isCompressed })}>
            <PlanGroupActionButton
              identifier={identifier}
              onChooseHandler={onChooseHandler}
              isCompressed={isCompressed}
              trackingData={trackingData}
            />
          </div>
          {isDesktop && index === 0 && (
            <StickyContentMarker
              stackIdentifier="secondary"
              topPositionPx={40}
              name="plan-group-footer"
              renderHeader={() => <PlanGroupStickyPlanFooter onChooseHandler={onChooseHandler} />}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default PlanGroupListing;
